import * as THREE from 'three';

const POOL = 20;
const SPAWN_DT = 0.045;
const GRAVITY = 16;

interface Drop {
  mesh: THREE.Mesh;
  vel: THREE.Vector3;
  life: number;
  active: boolean;
}

interface Emitter {
  origin: THREE.Vector3;
  drops: Drop[];
  acc: number;
  age: number;
  duration: number;
  stopLoop: (() => void) | null;
  stopped: boolean;
}

/** Duke-style looping bowl geyser. */
export class ToiletSpraySystem {
  private readonly emitters: Emitter[] = [];
  private readonly geo = new THREE.BoxGeometry(0.016, 0.038, 0.016);
  private readonly mat = new THREE.MeshBasicMaterial({
    color: 0xa8d4e8,
    transparent: true,
    opacity: 0.72,
    depthWrite: false,
  });

  constructor(private readonly scene: THREE.Object3D) {}

  start(origin: THREE.Vector3, opts: { duration?: number; stopLoop?: () => void } = {}): void {
    const drops: Drop[] = [];
    for (let i = 0; i < POOL; i++) {
      const mesh = new THREE.Mesh(this.geo, this.mat);
      mesh.visible = false;
      mesh.userData.ignoreHit = true;
      mesh.frustumCulled = false;
      this.scene.add(mesh);
      drops.push({ mesh, vel: new THREE.Vector3(), life: 0, active: false });
    }
    this.emitters.push({
      origin: origin.clone(),
      drops,
      acc: 0,
      age: 0,
      duration: opts.duration ?? 5.2,
      stopLoop: opts.stopLoop ?? null,
      stopped: false,
    });
  }

  update(dt: number): void {
    for (const e of this.emitters) {
      e.age += dt;
      const spraying = e.age < e.duration;
      if (spraying) {
        e.acc += dt;
        while (e.acc >= SPAWN_DT) {
          e.acc -= SPAWN_DT;
          spawnDrop(e);
        }
      } else if (!e.stopped) {
        e.stopped = true;
        e.stopLoop?.();
        e.stopLoop = null;
      }
      for (const d of e.drops) {
        if (!d.active) continue;
        d.vel.y -= GRAVITY * dt;
        d.mesh.position.addScaledVector(d.vel, dt);
        const spd = d.vel.length();
        d.mesh.scale.set(1, 1 + Math.min(1.4, spd * 0.12), 1);
        d.life -= dt;
        if (d.mesh.position.y < 0.03 || d.life <= 0) {
          d.active = false;
          d.mesh.visible = false;
        }
      }
    }
  }
}

function spawnDrop(e: Emitter): void {
  const d = e.drops.find((x) => !x.active);
  if (!d) return;
  d.active = true;
  d.life = 0.65 + Math.random() * 0.25;
  d.mesh.visible = true;
  d.mesh.position.copy(e.origin);
  d.vel.set((Math.random() - 0.5) * 0.7, 3.1 + Math.random() * 1.5, (Math.random() - 0.5) * 0.7);
}

export function burstToilet(toilet: THREE.Object3D): boolean {
  if (toilet.userData.destroyed) return false;
  toilet.userData.destroyed = true;
  const lid = toilet.userData.lid as THREE.Object3D | undefined;
  if (lid) {
    lid.rotation.x = -0.85;
    lid.position.y += 0.04;
    lid.position.z -= 0.06;
  }
  toilet.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const m of mats) {
      const std = m as THREE.MeshStandardMaterial;
      if (std.isMeshStandardMaterial) std.color.offsetHSL(0, -0.04, -0.12);
    }
  });
  return true;
}
